<template>
  <div :class="containerClass">
    <div
      v-for="(group, idx) in groupsView"
      :key="idx"
      :class="rowClass"
    >
      <!-- Between-group AND/OR toggle — sits on the same line as the following group -->
      <button
        v-if="idx > 0"
        @click.stop="toggleGroupsMode"
        type="button"
        class="self-center inline-flex items-center px-2 py-1 text-[11px] font-semibold rounded-full border transition-colors shrink-0 leading-none"
        :class="groupsMode === 'and'
          ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
          : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'"
        :title="groupsMode === 'and' ? 'AND: must match all groups' : 'OR: match any group'"
      >
        {{ groupsMode.toUpperCase() }}
      </button>
      <div :class="groupBoxClass">
        <InfoTagFilter
          :modelValue="group.include"
          :excludedTags="group.exclude"
          :filterMode="group.mode"
          :enableAdvancedFilters="true"
          :infoTagType="infoTagType"
          :label="label"
          :buttonLabel="buttonLabel"
          :color="color"
          @update:modelValue="(v) => updateGroup(idx, 'include', v)"
          @update:excludedTags="(v) => updateGroup(idx, 'exclude', v)"
          @update:filterMode="(v) => updateGroup(idx, 'mode', v)"
          @change="emitChange"
        />
        <button
          @click.stop="removeGroup(idx)"
          type="button"
          class="ml-auto text-gray-400 hover:text-red-500 leading-none text-base px-1"
          title="Remove group"
        >&times;</button>
      </div>
    </div>
    <button
      v-if="canAddGroup"
      @click.stop="addGroup"
      type="button"
      class="self-center inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
      title="Add another group"
    >
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Group
    </button>
  </div>
</template>

<script>
import InfoTagFilter from './InfoTagFilter.vue'

const EMPTY_GROUP = () => ({ include: [], mode: 'or', exclude: [] })

export default {
  name: 'GroupedInfoTagFilter',
  components: { InfoTagFilter },
  props: {
    modelValue: {
      type: Array,
      default: () => []
      // Array of { include: [tagId], mode: 'or'|'and', exclude: [tagId] }.
      // include and exclude are disjoint — see InfoTagFilter contract.
    },
    groupsMode: {
      type: String,
      default: 'or'
      // 'or' = match any group, 'and' = match every group
    },
    infoTagType: { type: String, default: null },
    label: { type: String, default: 'Filter by Tag' },
    buttonLabel: { type: String, default: 'Tag' },
    color: { type: String, default: 'emerald' },
    direction: {
      type: String,
      default: 'vertical',
      validator: v => ['vertical', 'horizontal'].includes(v)
      // 'horizontal' lays groups out inline-wrap so the filter sits on a
      // shared toolbar row; 'vertical' stacks for a dedicated panel.
    }
  },
  emits: ['update:modelValue', 'update:groupsMode', 'change'],
  data() {
    return {
      // Holds the next-state across a synchronous chain of child emits.
      // InfoTagFilter operations like toggleExclude and clearAll fire two
      // emits back-to-back; without this cache the second handler reads
      // stale props and clobbers the first handler's write.
      pendingGroups: null
    }
  },
  computed: {
    groupsView() {
      // Always show at least one (empty) group so the user has somewhere to start.
      if (!this.modelValue || this.modelValue.length === 0) {
        return [EMPTY_GROUP()]
      }
      return this.modelValue
    },
    canAddGroup() {
      const last = this.groupsView[this.groupsView.length - 1]
      return last && ((last.include?.length || 0) > 0 || (last.exclude?.length || 0) > 0)
    },
    isHorizontal() {
      return this.direction === 'horizontal'
    },
    containerClass() {
      return this.isHorizontal
        ? 'flex flex-row flex-wrap items-center gap-1'
        : 'flex flex-col items-stretch gap-1'
    },
    rowClass() {
      return this.isHorizontal
        ? 'flex items-center gap-2'
        : 'flex items-center gap-2 w-full'
    },
    groupBoxClass() {
      return this.isHorizontal
        ? 'flex items-center gap-1'
        : 'grow flex items-center gap-1'
    }
  },
  methods: {
    emitGroups(groups) {
      this.$emit('update:modelValue', groups)
    },
    emitChange() {
      this.$emit('change', this.modelValue)
    },
    updateGroup(idx, field, value) {
      const base = this.pendingGroups || this.groupsView
      const next = base.map((g, i) =>
        i === idx ? { ...g, [field]: value } : g
      )
      this.pendingGroups = next
      this.emitGroups(next)
      this.$nextTick(() => { this.pendingGroups = null })
    },
    addGroup() {
      this.emitGroups([...this.groupsView, EMPTY_GROUP()])
    },
    removeGroup(idx) {
      const next = this.groupsView.filter((_, i) => i !== idx)
      this.emitGroups(next)
      this.emitChange()
    },
    toggleGroupsMode() {
      this.$emit('update:groupsMode', this.groupsMode === 'or' ? 'and' : 'or')
      this.emitChange()
    }
  }
}
</script>
